import { getWebRelayStateDisplayName, IWebRelayConfig, IWebRelayEndpointConfig, OptionValue, WebRelayClientEndpoint, WebRelayConfig, WebRelayEndpointConfig, WebRelayModel, WebRelayState } from "@app/shared";
import { EnumSelect, Input, InputRow, Select } from "@app/dashboard/src/components/shared/inputs";
import { normalizeWebRelayEndpointConfig, WEB_RELAY_FIRE_EXPRESSION } from "../../../helpers/webRelayEndpointConfig";
import { EditEndpointConfigProps } from "./EditEndpointConfig";

type EditWebRelayEndpointConfigProps = {
} & EditEndpointConfigProps;

const getNumberArray = (start: number, end: number) => {
    return Array.from({ length: (end - start + 1) }, (_, i) => i + start)
}
const getNumberOptions = (start: number, end: number) => {
    return getNumberArray(start, end).map(i => new OptionValue({ label: i + "", value: i + "" }));
}

const getNumRelayOptions = (model: WebRelayModel | undefined) => {
    // Auto is not known to be a single until the device has asked the board, so offer the full range
    // rather than silently capping a quad's wiring at one relay.
    if (model === WebRelayModel.Quad_400_Series || model === WebRelayModel.Quad_Original || model === WebRelayModel.Auto)
        return getNumberOptions(1, 4);

    return getNumberOptions(1, 1);
};

export const EditWebRelayEndpointConfig: React.FC<EditWebRelayEndpointConfigProps> = (props) => {

    // Normalised rather than parsed raw: a blob written in the contract's own PascalCase reads as
    // "no relays" to the generated model, and the count select below then fills the gap with a blank
    // "relay 1, Off" row that a save would store beside the real entry.
    const config = new WebRelayEndpointConfig(normalizeWebRelayEndpointConfig(props.value) as IWebRelayEndpointConfig)

    const updateParent = () => {
        props.onChange(JSON.stringify(config))
    };

    // Rows added here start with the fire-on-every-event Evaluation rather than a blank one: the
    // device skips a blank expression outright, so a relay added and saved as-is used to be one
    // that never fired — the same silent shape the cab path produced before RelayWiring fixed it.
    const onDetectorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCount = parseInt(e.target.value);
        if ((config.relays?.length ?? 0) < 1) {
            config.relays = [new WebRelayConfig({ relay: 1, expression: WEB_RELAY_FIRE_EXPRESSION } as IWebRelayConfig)];
        }

        if (config.relays!.length > newCount) {
            config.relays = config.relays?.splice(0, newCount);
        } else if (config.relays!.length < newCount) {
            config.relays = [...config.relays!, ...getNumberArray(config.relays!.length + 1, newCount).map(i => new WebRelayConfig({ relay: i, expression: WEB_RELAY_FIRE_EXPRESSION } as IWebRelayConfig))];
        }
        updateParent();
    }

    const relayModel = (props.endpoint as WebRelayClientEndpoint)?.model;

    return (<>
        <InputRow><Select label="Number of Relays" onChange={onDetectorChange} value={config.relays?.length ?? 0} options={getNumRelayOptions(relayModel)} /></InputRow>
        {
            config.relays?.map((d, i) => <InputRow key={i + "-" + d}>
                <div className="grid grid-cols-3 gap-5">
                    <div><Input label="Relay#" value={d.relay} onChange={(e) => { d.relay = parseInt(e.target.value); updateParent(); }}></Input></div>
                    <div><EnumSelect nameToDisplayConverter={getWebRelayStateDisplayName} enumType={WebRelayState} label="State to Send" value={d.stateToSend} onChange={(e) => { d.stateToSend = e as WebRelayState; updateParent(); }} /></div>
                    {d.stateToSend === WebRelayState.PulseOrReboot &&
                        <div><Input label="Pulse Time" value={d.pulseTime} onChange={(e) => { d.pulseTime = parseFloat(e.target.value); updateParent(); }}></Input></div>
                    }
                    <div className="col-span-2"><Input label="Evaluation" value={d.expression ?? ""} onChange={(e) => { d.expression = e.target.value; updateParent(); }}></Input></div>
                </div>
            </InputRow>)
        }
    </>
    );
}
